import React, { useEffect, useState } from 'react';
import { Button, Result, Space, Spin } from 'antd';
import {
  getCurrentSession,
  buildLoginRedirectUrl,
  getLoginUrl,
  logoutSession,
} from '../api/auth';
import { AuthProvider } from '../context/AuthContext';
import type { Session } from '../api/auth';
import { hasKfcAccess } from '../utils/permissions';

interface AuthGuardProps {
  children: React.ReactNode;
}

/**
 * 进入 KFC 页面前校验 Kratos 会话与履行中心权限（koc_fulfilling）。
 * 未登录 → 跳转 auth 登录页并带回 returnUrl。
 * 已登录但无权限 → 展示拒绝页（不带 returnUrl 回登录，避免死循环）。
 */
const AuthGuard: React.FC<AuthGuardProps> = ({ children }) => {
  const [checking, setChecking] = useState(true);
  const [session, setSession] = useState<Session | null>(null);
  const [denied, setDenied] = useState(false);
  const [loggingOut, setLoggingOut] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const currentSession = await getCurrentSession();
        if (cancelled) return;
        if (!currentSession) {
          window.location.href = buildLoginRedirectUrl();
          return;
        }
        if (!hasKfcAccess(currentSession.identity?.traits)) {
          setSession(currentSession);
          setDenied(true);
          return;
        }
        setSession(currentSession);
      } catch {
        if (!cancelled) {
          setSession(null);
        }
      } finally {
        if (!cancelled) setChecking(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const goAuthConsole = () => {
    const loginUrl = getLoginUrl();
    // 去掉 /login，进入认证中心首页/dashboard
    const base = loginUrl.replace(/\/login\/?$/i, '');
    window.location.href = base || loginUrl;
  };

  const handleLogout = async () => {
    const loginUrl = getLoginUrl();
    setLoggingOut(true);
    try {
      await logoutSession(loginUrl);
      window.location.href = loginUrl;
    } catch (error) {
      console.error('Logout failed:', error);
      window.location.href = loginUrl;
    } finally {
      setLoggingOut(false);
    }
  };

  if (checking) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <Spin size="large" tip="正在验证身份..." />
      </div>
    );
  }

  if (denied) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <Result
          status="403"
          title="无权访问履行中心"
          subTitle="当前账号没有履行中心（KFC）访问权限。如需开通，请联系管理员。"
          extra={
            <Space>
              <Button type="primary" onClick={goAuthConsole}>
                返回认证中心
              </Button>
              <Button loading={loggingOut} onClick={handleLogout}>
                退出登录
              </Button>
            </Space>
          }
        />
      </div>
    );
  }

  if (!session) {
    return null;
  }

  return <AuthProvider session={session}>{children}</AuthProvider>;
};

export default AuthGuard;
